/**
 * Shared overlay, scroll lock, inert, Escape handling, and trigger delegation
 * for modal-slideout, cart-drawer, and Bloomreach .br-slideout-root.
 */
const SlideoutManager = {
  Z: {
    overlay: 1000,
    panel: 1001,
    host: 1001,
    portaled: 1002,
  },

  overlay: null,
  _overlayBound: false,
  _escapeBound: false,
  _triggerBound: false,
  _brOpenCount: 0,

  init() {
    this.getSharedOverlay();
    this.bindOverlayOnce();
    this.bindGlobalEscapeOnce();
    this.bindTriggerDelegationOnce();
  },

  isMobile() {
    return window.utilities?.isMobileBreakpoint?.() ?? window.innerWidth < 964;
  },

  hasOpenThemeModal() {
    const selector = this.isMobile()
      ? 'modal-slideout[open]'
      : 'modal-slideout[open]:not(.modal-slideout--inline-popover)';
    return Boolean(document.querySelector(selector));
  },

  hasOpenCartDrawer() {
    return Boolean(document.querySelector('cart-drawer[open]'));
  },

  hasOpenBrSlideout() {
    return this._brOpenCount > 0 || Boolean(document.querySelector('.br-slideout-root[open]'));
  },

  shouldShowOverlay() {
    if (this.hasOpenThemeModal()) return true;
    if (this.hasOpenBrSlideout()) return true;
    if (this.hasOpenCartDrawer() && !this.isMobile()) return true;
    return false;
  },

  shouldLockScroll() {
    return this.shouldShowOverlay() || (this.hasOpenCartDrawer() && this.isMobile());
  },

  getSharedOverlay() {
    if (this.overlay?.isConnected) return this.overlay;

    const existing = document.querySelectorAll('.slideout-modal-overlay');
    existing.forEach((node, index) => {
      if (index > 0) node.remove();
    });

    let overlay = document.querySelector('.slideout-modal-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.classList.add('slideout-modal-overlay');
      overlay.style.display = 'none';
      document.body.appendChild(overlay);
    }

    this.overlay = overlay;

    if (typeof CartDrawer !== 'undefined') {
      CartDrawer.overlay = overlay;
    }
    if (typeof ModalSlideout !== 'undefined') {
      ModalSlideout.overlay = overlay;
    }

    this.bindOverlayOnce();
    return overlay;
  },

  bindOverlayOnce() {
    const overlay = this.getSharedOverlay();
    if (overlay.dataset.slideoutOverlayBound) return;

    overlay.dataset.slideoutOverlayBound = 'true';
    overlay.addEventListener('click', () => {
      document.querySelectorAll('modal-slideout[open]').forEach((modal) => modal.close?.());
      if (!document.querySelector('modal-slideout[open]')) {
        document.querySelectorAll('cart-drawer[open]').forEach((drawer) => drawer.close?.());
      }
      this.sync();
    });
  },

  bindGlobalEscapeOnce() {
    if (this._escapeBound) return;
    this._escapeBound = true;

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      const openModals = [...document.querySelectorAll('modal-slideout[open]')];
      const topModal = openModals[openModals.length - 1];
      if (topModal) {
        const closeOnEsc = topModal.getAttribute('close-on-esc') !== 'false';
        if (closeOnEsc) {
          event.preventDefault();
          topModal.close();
        }
        return;
      }

      if (this.hasOpenBrSlideout()) return;

      const drawer = document.querySelector('cart-drawer[open]');
      if (drawer) {
        event.preventDefault();
        drawer.close();
      }
    });
  },

  bindTriggerDelegationOnce() {
    if (this._triggerBound) return;
    this._triggerBound = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[aria-controls]');
      if (!trigger) return;

      const id = trigger.getAttribute('aria-controls');
      if (!id) return;

      const modal = document.getElementById(id);
      if (!modal || typeof modal.open !== 'function' || modal.tagName !== 'MODAL-SLIDEOUT') return;

      const containingModal = trigger.closest('modal-slideout');
      if (containingModal?.id === id) return;

      const tag = trigger.tagName;
      if (tag === 'A' && trigger.getAttribute('href')) return;

      event.preventDefault();
      modal.open(trigger);
    });
  },

  registerBrOpen() {
    this._brOpenCount += 1;
    this.sync();
  },

  unregisterBrOpen() {
    this._brOpenCount = Math.max(0, this._brOpenCount - 1);
    this.sync();
  },

  getMainContentRoot() {
    return document.querySelector('main, #MainContent, [role="main"]');
  },

  syncInert() {
    const root = this.getMainContentRoot();
    if (!root || !('inert' in root)) return;
    root.inert = this.shouldLockScroll();
  },

  syncBodyScrollLock() {
    const lock = this.shouldLockScroll();
    document.body.classList.toggle('overflow-hidden', lock);
    document.documentElement.classList.toggle('slideout-scroll-locked', lock);
  },

  syncOverlayLayer() {
    const overlay = this.getSharedOverlay();
    const portaledModal = document.querySelector('modal-slideout[data-portal-body][open]');
    const cartBehindModal = this.hasOpenCartDrawer() && Boolean(portaledModal);

    overlay.toggleAttribute('data-layer-portaled', Boolean(portaledModal));
    overlay.toggleAttribute('data-layer-cart-stack', cartBehindModal);
  },

  syncOverlayVisibility() {
    const overlay = this.getSharedOverlay();
    overlay.style.display = this.shouldShowOverlay() ? 'block' : 'none';
    this.syncOverlayLayer();
    this.syncBodyScrollLock();
    this.syncInert();
  },

  sync() {
    this.syncOverlayVisibility();
  },

  insertHostBeforeOverlay(host) {
    const overlay = this.getSharedOverlay();
    if (host.parentElement !== document.body) {
      document.body.insertBefore(host, overlay);
    }
  },
};

SlideoutManager.init();
window.SlideoutManager = SlideoutManager;

// Back-compat aliases used by cart.js and legacy code
window.ModalSlideout = window.ModalSlideout || {};
ModalSlideout.getSharedOverlay = () => SlideoutManager.getSharedOverlay();
ModalSlideout.syncOverlayVisibility = () => SlideoutManager.sync();
